import React, { useCallback, useEffect, useRef, useState } from "react";
import { Clapperboard, Download, Loader2, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

// Records the 3D runner's ~7s cinematic auto-orbit trailer.
// - Manual: opens a preview dialog to Set / Download.
// - Auto (one-tap, via registerGenerate): records → uploads → sets, no dialog.
export default function TrailerButton({ canvasRef, gameId, title, onSaved, registerGenerate }) {
  const [phase, setPhase] = useState("idle"); // idle | recording | saving | ready
  const [open, setOpen] = useState(false);
  const [clip, setClip] = useState(null); // { url, mime, size }
  const [saving, setSaving] = useState(false);
  const activeRef = useRef(false);
  const autoRef = useRef(false);
  const urlRef = useRef(null);

  const revoke = () => { if (urlRef.current) { URL.revokeObjectURL(urlRef.current); urlRef.current = null; } };
  const ext = clip && clip.mime && clip.mime.indexOf("mp4") >= 0 ? "mp4" : "webm";
  const slug = (title || "gameview").replace(/[^a-z0-9]+/gi, "-").toLowerCase().replace(/^-+|-+$/g, "") || "gameview";

  const uploadBlob = useCallback(async (blob, mime) => {
    const e = (mime || blob.type || "").indexOf("mp4") >= 0 ? "mp4" : "webm";
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("clip", blob, `${slug}-trailer.${e}`);
      const { data } = await api.post(`/games/${gameId}/trailer`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Set as your game trailer! 🎬");
      if (onSaved) onSaved(data.trailer_clip_url);
      return true;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't save the trailer.");
      return false;
    } finally { setSaving(false); }
  }, [gameId, slug, onSaved]);

  useEffect(() => {
    const handler = async (ev) => {
      const d = ev.data || {};
      if (!activeRef.current) return;
      if (d.type === "trailer-done") {
        activeRef.current = false;
        if (!d.blob || !d.size) { autoRef.current = false; setPhase("idle"); toast.error("The trailer came out empty — try again."); return; }
        if (autoRef.current) {
          autoRef.current = false;
          setPhase("saving");
          await uploadBlob(d.blob, d.mime);
          setPhase("idle");
        } else {
          revoke();
          const url = URL.createObjectURL(d.blob);
          urlRef.current = url;
          setClip({ url, mime: d.mime || "video/webm", size: d.size });
          setPhase("ready"); setOpen(true);
        }
      } else if (d.type === "trailer-error") {
        activeRef.current = false; autoRef.current = false; setPhase("idle");
        toast.error(d.message || "Couldn't record the trailer.");
      }
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, [uploadBlob]);

  useEffect(() => () => revoke(), []);

  const record = useCallback((auto = false) => {
    if (!canvasRef?.current?.recordTrailer) { toast.error("Let the 3D preview finish loading first."); return false; }
    revoke(); setClip(null); autoRef.current = !!auto; activeRef.current = true; setPhase("recording");
    toast.message("🎬 Filming your cinematic trailer… (~7s)");
    canvasRef.current.recordTrailer();
    setTimeout(() => { if (activeRef.current) { activeRef.current = false; autoRef.current = false; setPhase("idle"); } }, 20000);
    return true;
  }, [canvasRef]);

  useEffect(() => { if (registerGenerate) registerGenerate(() => record(true)); }, [registerGenerate, record]);

  const setAsTrailer = async () => {
    if (!clip || saving) return;
    const resp = await fetch(clip.url);
    const blob = await resp.blob();
    const ok = await uploadBlob(blob, clip.mime);
    if (ok) setOpen(false);
  };

  const download = () => {
    if (!clip) return;
    const a = document.createElement("a");
    a.href = clip.url; a.download = `${slug}-trailer.${ext}`;
    document.body.appendChild(a); a.click(); a.remove();
    toast.success("Trailer downloaded!");
  };

  const sizeLabel = clip ? (clip.size >= 1048576 ? `${(clip.size / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(clip.size / 1024))} KB`) : "";
  const busyPhase = phase === "recording" || phase === "saving";

  return (
    <>
      {busyPhase ? (
        <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-500/15 border border-amber-500/40 text-amber-500 text-xs font-semibold" data-testid="trailer-recording">
          <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" /> {phase === "saving" ? "Saving trailer…" : "Filming cinematic trailer…"}
        </div>
      ) : (
        <Button variant="outline" size="sm" onClick={() => record(false)}
          className="rounded-full gap-1.5 border-amber-500/40 text-amber-500 hover:bg-amber-500/10" data-testid="auto-trailer-btn">
          <Clapperboard className="w-4 h-4" /> Auto-trailer
        </Button>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="trailer-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Clapperboard className="w-5 h-5 text-amber-500" /> Your cinematic trailer</DialogTitle>
            <DialogDescription>A ~7-second golden-hour flythrough of your world. Set it as your game's trailer or download it to post anywhere.</DialogDescription>
          </DialogHeader>
          {clip && (
            <video src={clip.url} controls autoPlay loop muted playsInline
              className="w-full rounded-xl border border-[hsl(var(--border))] bg-black" style={{ aspectRatio: "16/9" }} data-testid="trailer-video" />
          )}
          <div className="flex flex-wrap gap-2">
            <Button onClick={setAsTrailer} disabled={saving} className="rounded-full gap-1.5 flex-1 bg-gradient-to-r from-amber-500 to-orange-500 text-white hover:opacity-90" data-testid="trailer-set-btn">
              {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</> : <><Sparkles className="w-4 h-4" /> Set as game trailer</>}
            </Button>
            <Button variant="outline" onClick={download} className="rounded-full gap-1.5" data-testid="trailer-download-btn">
              <Download className="w-4 h-4" /> Download{sizeLabel ? ` (${sizeLabel})` : ""}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
